import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, Speaker, Sliders, Network, Radio } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { FIELD_PHOTOS, SKILL_GROUPS } from "@/data/content";

const ICONS = { Mic, Speaker, Sliders, Network, Radio };

const ConsoleBrands = ({ groups }) => {
  const [active, setActive] = useState(groups[0].brand);
  const current = groups.find((g) => g.brand === active);
  return (
    <div>
      <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Console brands">
        {groups.map((g) => (
          <button
            key={g.brand}
            type="button"
            role="tab"
            aria-selected={active === g.brand}
            data-testid={`console-brand-${g.brand.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setActive(g.brand)}
            className={`rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.12em] transition-[background-color,color,border-color,box-shadow] duration-300 ${
              active === g.brand
                ? "bg-electric text-[#050505] shadow-[0_0_20px_rgba(0,163,255,0.45)]"
                : "border border-white/10 bg-white/[0.03] text-neutral-400 hover:border-electric/60 hover:text-electric hover:shadow-[0_0_16px_rgba(0,163,255,0.25)]"
            }`}
          >
            {g.brand}
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.ul
          key={active}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
          className="mt-4 grid grid-cols-2 gap-2"
          data-testid="console-models-list"
        >
          {current.models.map((m) => (
            <li key={m} className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm font-semibold text-neutral-300 transition-colors duration-300 hover:border-electric/50 hover:text-electric">
              {m}
            </li>
          ))}
        </motion.ul>
      </AnimatePresence>
    </div>
  );
};

export default function Skills() {
  const [viewerIndex, setViewerIndex] = useState(null);
  return (
    <section id="skills" data-testid="skills-section" className="border-t border-white/5 bg-[#0A0A0A] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          label="Skills & Technology"
          title="Tools of"
          accent="the trade"
          description="Hands-on, show-ready expertise across consoles, systems and networked audio."
          testId="skills-heading"
        />

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="skills-grid">
          {SKILL_GROUPS.map((group, i) => {
            const Icon = ICONS[group.icon];
            return (
              <Reveal key={group.title} delay={0.06 * i}>
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.02] p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-electric/40">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-electric/30 bg-electric/10 text-electric">
                    <Icon size={22} aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-lg font-semibold uppercase tracking-tight text-neutral-100">{group.title}</h3>
                  {group.groups ? (
                    <ConsoleBrands groups={group.groups} />
                  ) : (
                    <ul className="mt-4 space-y-2.5">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-center gap-2.5 text-sm text-neutral-400">
                          <span className="h-1 w-1 rounded-full bg-electric" aria-hidden="true" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <p className="mb-6 mt-16 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-electric" data-testid="field-photos-label">
            <span className="h-px w-10 bg-electric" aria-hidden="true" />
            In the field
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="field-photos-grid">
            {FIELD_PHOTOS.map((p, idx) => (
              <button
                key={p.image}
                type="button"
                onClick={() => setViewerIndex(idx)}
                data-testid={`field-photo-${p.image.split("/").pop().split(".")[0]}`}
                aria-label={`View full-screen: ${p.caption}`}
                className="group relative cursor-zoom-in overflow-hidden rounded-xl border border-white/10 text-left transition-[border-color] duration-300 hover:border-electric/50"
              >
                <img
                  src={p.image}
                  alt={p.caption}
                  loading="lazy"
                  className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-3 pb-2.5 pt-8 text-[11px] font-semibold leading-snug text-neutral-200">
                  {p.caption}
                </span>
              </button>
            ))}
          </div>
        </Reveal>
      </div>
      <Lightbox items={FIELD_PHOTOS} index={viewerIndex} onClose={() => setViewerIndex(null)} onNavigate={setViewerIndex} />
    </section>
  );
}
